import { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WelcomeGuide({ user, onDismiss }) {
  const [step, setStep] = useState(0);
  const [hasData, setHasData] = useState({ bills: false, income: false, goals: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUserData();
  }, []);

  const checkUserData = async () => {
    try {
      const [billsRes, incomeRes, goalsRes] = await Promise.all([
        fetch(`${API}/bills`, { credentials: 'include' }),
        fetch(`${API}/income`, { credentials: 'include' }),
        fetch(`${API}/goals`, { credentials: 'include' })
      ]);
      
      const bills = billsRes.ok ? await billsRes.json() : [];
      const income = incomeRes.ok ? await incomeRes.json() : [];
      const goals = goalsRes.ok ? await goalsRes.json() : [];
      
      setHasData({
        bills: Array.isArray(bills) && bills.length > 0,
        income: Array.isArray(income) && income.length > 0,
        goals: Array.isArray(goals) && goals.length > 0
      });
    } catch (error) {
      console.error('Error checking user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: "Welcome to GuapGoals! 🎉",
      description: "Let's get you set up in just a few steps. This app will help you take control of your finances.",
      icon: "👋",
      action: null
    },
    {
      title: "Step 1: Add Your Income",
      description: "Start by adding your income sources. Go to the Income page and enter your paycheck details, side income, or any other money coming in.",
      icon: "💵",
      link: "/income",
      linkText: "Go to Income →",
      completed: hasData.income
    },
    {
      title: "Step 2: Add Your Bills",
      description: "Next, add your recurring bills like rent, utilities, subscriptions, and loans. This helps us calculate how much you have left over each pay period.",
      icon: "📝",
      link: "/bills",
      linkText: "Go to Bills →",
      completed: hasData.bills
    },
    {
      title: "Step 3: Set Your Goals",
      description: "Create savings goals for things like a vacation, new car, or a big purchase. We'll help you track your progress!",
      icon: "🎯",
      link: "/goals",
      linkText: "Go to Goals →",
      completed: hasData.goals
    },
    {
      title: "You're All Set! 🚀",
      description: "Great job! Now visit your Dashboard to see your budget breakdown and decide what to do with your leftover money.",
      icon: "✅",
      link: "/dashboard",
      linkText: "Go to Dashboard →",
      action: null
    }
  ];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  const currentStep = steps[step];
  const allSetupComplete = hasData.bills && hasData.income && hasData.goals;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl">
        {/* Progress bar */}
        <div className="h-2 bg-gray-200">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>

        <div className="p-6">
          {/* Step indicator */}
          <div className="flex justify-between mb-6">
            {steps.map((_, idx) => (
              <div 
                key={idx}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  idx < step ? 'bg-green-500 text-white' :
                  idx === step ? 'bg-blue-500 text-white' :
                  'bg-gray-200 text-gray-500'
                }`}
              >
                {idx < step ? '✓' : idx + 1}
              </div>
            ))}
          </div>

          {/* Content */}
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">{currentStep.icon}</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentStep.title}</h2>
            <p className="text-gray-600">{currentStep.description}</p>
            
            {currentStep.completed && (
              <div className="mt-3 inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                <span>✓</span> Already completed!
              </div>
            )}
          </div>

          {/* Quick Tips */}
          {step === 0 && (
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">🌟 Quick Tips</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>Dashboard</strong> shows your pay period breakdown</li>
                <li>• <strong>Action Engine</strong> helps you decide where to put extra money</li>
                <li>• <strong>Debt Snowball</strong> creates a payoff plan for your debts</li>
                <li>• <strong>Envelope Challenge</strong> is a fun way to save!</li>
              </ul>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={onDismiss}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              Skip for now
            </button>

            <div className="flex gap-2">
              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  ← Back
                </button>
              )}
              
              {currentStep.link && (
                <a
                  href={currentStep.link}
                  onClick={onDismiss}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  {currentStep.linkText}
                </a>
              )}
              
              {step < steps.length - 1 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                >
                  {step === 0 ? "Let's Go!" : "Next →"}
                </button>
              ) : (
                <button
                  onClick={onDismiss}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  Get Started!
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
